import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { TripComparison } from './use-trip-comparison';

export interface TripsComparisonFilters {
  startDate?: string;
  endDate?: string;
  vehicleId?: string;
  minAdherence?: number;
  maxAdherence?: number;
  status?: 'all' | 'matched' | 'unmatched';
}

export function useTripsComparisonList(
  companyId?: string,
  filters?: TripsComparisonFilters
) {
  return useQuery({
    queryKey: ['trips-comparison-list', companyId, filters],
    queryFn: async () => {
      if (!companyId) return [];

      // Buscar todas as viagens planejadas
      let query = supabase
        .from('fleet_trips')
        .select(`
          id,
          origin,
          destination,
          eta_start,
          eta_end,
          distance_planned_km,
          status,
          fleet_vehicles(plate),
          fleet_drivers(name)
        `)
        .eq('company_id', companyId)
        .order('eta_start', { ascending: false });

      if (filters?.startDate) {
        query = query.gte('eta_start', filters.startDate);
      }
      if (filters?.endDate) {
        query = query.lte('eta_start', filters.endDate);
      }
      if (filters?.vehicleId) {
        query = query.eq('vehicle_id', filters.vehicleId);
      }

      const { data: fleetTrips, error } = await query;

      if (error) throw error;

      // Para cada viagem, buscar comparativo
      const comparisons = await Promise.all(
        (fleetTrips || []).map(async (trip) => {
          const { data: comparison } = await (supabase as any)
            .rpc('get_trip_comparison', {
              p_fleet_trip_id: trip.id
            });

          return comparison?.[0] as TripComparison;
        })
      );

      // Aplicar filtros adicionais
      let filtered = comparisons.filter(Boolean);

      if (filters?.status === 'matched') {
        filtered = filtered.filter(c => c.gps_trip_id);
      } else if (filters?.status === 'unmatched') {
        filtered = filtered.filter(c => !c.gps_trip_id);
      }

      if (filters?.minAdherence !== undefined) {
        filtered = filtered.filter(
          c => c.adherence_score && c.adherence_score >= filters.minAdherence!
        );
      }

      if (filters?.maxAdherence !== undefined) {
        filtered = filtered.filter(
          c => c.adherence_score && c.adherence_score <= filters.maxAdherence!
        );
      }

      return filtered;
    },
    enabled: !!companyId,
    refetchInterval: 60000, // Refetch every minute
  });
}
